import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaCopy, FaCheck, FaTrash, FaGripVertical, FaFlask, FaVolumeUp, FaTimes } from 'react-icons/fa';
// FaVolumeUp, FaTimes kept for potential future use
import config from '../../config';

const CHECKERBOARD_STYLE = {
  backgroundImage:
    'linear-gradient(45deg, #1a1a1a 25%, transparent 25%), linear-gradient(-45deg, #1a1a1a 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1a1a1a 75%), linear-gradient(-45deg, transparent 75%, #1a1a1a 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
  backgroundColor: '#0d0d0d',
};

const LAYER_TYPES = [
  {
    type: 'recent_tips',
    label: 'Recent Tips',
    description: 'Scrolling list of latest supporters',
    defaultWidth: 400,
    defaultHeight: 500,
    defaultX: 50,
    defaultY: 50,
  },
  {
    type: 'tip_alert',
    label: 'Tip Alert',
    description: 'Animated popup for new tips',
    defaultWidth: 600,
    defaultHeight: 200,
    defaultX: 660,
    defaultY: 820,
  },
  {
    type: 'brick_counter',
    label: 'Brick Counter',
    description: 'Live count of bricks in your castle',
    defaultWidth: 300,
    defaultHeight: 120,
    defaultX: 50,
    defaultY: 50,
  },
  {
    type: 'tip_media',
    label: 'Tip Media',
    description: 'Video or audio that plays on tip',
    defaultWidth: 640,
    defaultHeight: 360,
    defaultX: 640,
    defaultY: 360,
  },
  {
    type: 'subathon_timer',
    label: 'Subathon Timer',
    description: 'Countdown timer — tips add time',
    defaultWidth: 620,
    defaultHeight: 120,
    defaultX: 55,
    defaultY: 81,
    defaultConfig: {
      start_time_minutes: 60,
      seconds_remaining: 3600,
      is_running: false,
      last_started_at: null,
      minutes_per_dollar: 1,
      auto_start: false,
      timer_size: 40,
      timer_color: '#ffffff',
      chip_color: '#22d3ee',
      accent_color: '#22d3ee',
      bg_color: 'rgba(10,12,20,0.72)',
      corner_radius: 14,
      show_event_info: true,
    },
  },
];

function layerTypeLabel(type) {
  const found = LAYER_TYPES.find((t) => t.type === type);
  return found ? found.label : type;
}

const toSlug = (str) =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'scene';

const LAYER_HINTS = {
  recent_tips: 'Latest supporters will appear here',
  tip_alert: 'Alert events will appear here',
  brick_counter: 'Live brick count will appear here',
  tip_media: 'Media plays here on tip',
  subathon_timer: 'Timer counts down — tips add time',
};

export default function OverlayEditor() {
  const { sceneId } = useParams();
  const navigate = useNavigate();

  const [scene, setScene] = useState(null);
  const [layers, setLayers] = useState([]);
  const [selectedLayerId, setSelectedLayerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedFlash, setSavedFlash] = useState(false);
  const [copiedObs, setCopiedObs] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [soundUploading, setSoundUploading] = useState(false);
  const soundInputRef = useRef(null);
  const [mediaUploading, setMediaUploading] = useState(false);
  const mediaInputRef = useRef(null);
  const [addMinutesInput, setAddMinutesInput] = useState(40);
  const [testBricksTotal, setTestBricksTotal] = useState(0);

  // Canvas sizing
  const canvasContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const [canvasScale, setCanvasScale] = useState(1);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });

  // Layer drag state (refs to avoid stale closure issues)
  const dragRef = useRef(null);

  const token = localStorage.getItem('access_token');

  // ─── Fetch scene ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchScene = async () => {
      try {
        const res = await fetch(`${config.API_URL}/overlay-scenes/${sceneId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setScene(data);
          setNameDraft(data.name);
          setLayers(data.layers || []);
        }
      } catch (err) {
        console.error('Error fetching scene:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchScene();
  }, [sceneId, token]);

  // ─── Measure container for scale ─────────────────────────────────────────
  const measureContainer = useCallback(() => {
    if (!canvasContainerRef.current) return;
    const rect = canvasContainerRef.current.getBoundingClientRect();
    setContainerSize({ w: rect.width, h: rect.height });
  }, []);

  useEffect(() => {
    measureContainer();
    const observer = new ResizeObserver(measureContainer);
    if (canvasContainerRef.current) observer.observe(canvasContainerRef.current);
    return () => observer.disconnect();
  }, [measureContainer]);

  useEffect(() => {
    if (!scene || !containerSize.w || !containerSize.h) return;
    const scale =
      Math.min(containerSize.w / scene.width, containerSize.h / scene.height) * 0.9;
    setCanvasScale(scale);
  }, [scene, containerSize]);

  // ─── Flash "Saved" ────────────────────────────────────────────────────────
  const flashSaved = () => {
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  };

  // ─── Save scene metadata ──────────────────────────────────────────────────
  const saveScene = useCallback(
    async (overrides = {}) => {
      if (!scene) return;
      const payload = { name: scene.name, width: scene.width, height: scene.height, background: scene.background, ...overrides };
      try {
        const res = await fetch(`${config.API_URL}/overlay-scenes/${sceneId}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) flashSaved();
      } catch (err) {
        console.error('Error saving scene:', err);
      }
    },
    [scene, sceneId, token]
  );

  // ─── Save layer ───────────────────────────────────────────────────────────
  const saveLayer = useCallback(
    async (layer) => {
      try {
        const res = await fetch(`${config.API_URL}/overlay-scenes/${sceneId}/layers/${layer.id}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: layer.name,
            x: layer.x,
            y: layer.y,
            width: layer.width,
            height: layer.height,
            z_index: layer.z_index,
            config: layer.config || {},
          }),
        });
        if (res.ok) flashSaved();
      } catch (err) {
        console.error('Error saving layer:', err);
      }
    },
    [sceneId, token]
  );

  // ─── Live overlay test controls ────────────────────────────────────────────
  // Each fires the *selected* layer directly (not "the first layer of this type"),
  // so testing one layer never affects another layer of the same type.
  const fireTipTest = useCallback(async (amount) => {
    if (!selectedLayerId) return;
    try {
      await fetch(`${config.API_URL}/overlay-scenes/${sceneId}/layers/${selectedLayerId}/fire-test-tip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ amount }),
      });
    } catch (err) {
      console.error('fireTipTest error:', err);
    }
  }, [selectedLayerId, sceneId, token]);

  const fireTestBricks = useCallback(async (delta) => {
    if (!selectedLayerId) return;
    const next = Math.max(0, testBricksTotal + delta);
    setTestBricksTotal(next);
    try {
      await fetch(`${config.API_URL}/overlay-scenes/${sceneId}/layers/${selectedLayerId}/fire-test-bricks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ bricks: next }),
      });
    } catch (err) {
      console.error('fireTestBricks error:', err);
    }
  }, [selectedLayerId, sceneId, token, testBricksTotal]);

  const clearTestBricks = useCallback(async () => {
    if (!selectedLayerId) return;
    setTestBricksTotal(0);
    try {
      await fetch(`${config.API_URL}/overlay-scenes/${sceneId}/layers/${selectedLayerId}/fire-test-bricks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ clear: true }),
      });
    } catch (err) {
      console.error('clearTestBricks error:', err);
    }
  }, [selectedLayerId, sceneId, token]);

  const fireTestDonation = useCallback(async (amount) => {
    if (!selectedLayerId) return;
    try {
      await fetch(`${config.API_URL}/overlay-scenes/${sceneId}/layers/${selectedLayerId}/fire-test-donation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: 'Test Donor', amount }),
      });
    } catch (err) {
      console.error('fireTestDonation error:', err);
    }
  }, [selectedLayerId, sceneId, token]);

  const subathonAction = useCallback(async (action, seconds = 0) => {
    if (!selectedLayerId) return;
    try {
      const res = await fetch(`${config.API_URL}/overlay-scenes/${sceneId}/layers/${selectedLayerId}/subathon`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, seconds }),
      });
      if (res.ok) {
        const updated = await res.json();
        setLayers((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
        flashSaved();
      }
    } catch (err) {
      console.error('Subathon action failed:', err);
    }
  }, [selectedLayerId, sceneId, token]);

  const updateLayerConfig = (key, value) => {
    setLayers((prev) =>
      prev.map((l) => (l.id === selectedLayerId ? { ...l, config: { ...l.config, [key]: value } } : l))
    );
  };

  // ─── Add layer ────────────────────────────────────────────────────────────
  const addLayer = async (typeInfo, dropX, dropY) => {
    const nextZ = layers.length > 0 ? Math.max(...layers.map((l) => l.z_index || 0)) + 1 : 1;
    const x = dropX !== undefined ? Math.round(dropX) : typeInfo.defaultX;
    const y = dropY !== undefined ? Math.round(dropY) : typeInfo.defaultY;
    const payload = {
      type: typeInfo.type,
      name: typeInfo.label,
      x,
      y,
      width: typeInfo.defaultWidth,
      height: typeInfo.defaultHeight,
      z_index: nextZ,
      config: typeInfo.defaultConfig || {},
    };
    try {
      const res = await fetch(`${config.API_URL}/overlay-scenes/${sceneId}/layers`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const newLayer = await res.json();
        setLayers((prev) => [...prev, newLayer]);
        setSelectedLayerId(newLayer.id);
      }
    } catch (err) {
      console.error('Error adding layer:', err);
    }
  };

  // ─── Delete layer ─────────────────────────────────────────────────────────
  const deleteLayer = async (layerId) => {
    try {
      const res = await fetch(`${config.API_URL}/overlay-scenes/${sceneId}/layers/${layerId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setLayers((prev) => prev.filter((l) => l.id !== layerId));
        if (selectedLayerId === layerId) setSelectedLayerId(null);
      }
    } catch (err) {
      console.error('Error deleting layer:', err);
    }
  };

  // ─── Layer drag (reposition on canvas) ───────────────────────────────────
  const handleLayerMouseDown = (e, layerId) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedLayerId(layerId);
    const layer = layers.find((l) => l.id === layerId);
    if (!layer) return;
    dragRef.current = {
      layerId,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startX: layer.x,
      startY: layer.y,
    };
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragRef.current) return;
      const { layerId, startMouseX, startMouseY, startX, startY } = dragRef.current;
      const dx = (e.clientX - startMouseX) / canvasScale;
      const dy = (e.clientY - startMouseY) / canvasScale;
      setLayers((prev) =>
        prev.map((l) =>
          l.id === layerId
            ? { ...l, x: Math.round(startX + dx), y: Math.round(startY + dy) }
            : l
        )
      );
    };

    const onMouseUp = () => {
      if (!dragRef.current) return;
      const { layerId } = dragRef.current;
      dragRef.current = null;
      setLayers((prev) => {
        const layer = prev.find((l) => l.id === layerId);
        if (layer) saveLayer(layer);
        return prev;
      });
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [canvasScale, saveLayer]);

  // ─── Widget palette drag-to-canvas ───────────────────────────────────────
  const handleWidgetDragStart = (e, typeInfo) => {
    e.dataTransfer.setData('widget-type', typeInfo.type);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleCanvasDragLeave = () => {
    setIsDragOver(false);
  };

  const handleCanvasDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const type = e.dataTransfer.getData('widget-type');
    const typeInfo = LAYER_TYPES.find((t) => t.type === type);
    if (!typeInfo || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const rawX = (e.clientX - rect.left) / canvasScale - typeInfo.defaultWidth / 2;
    const rawY = (e.clientY - rect.top) / canvasScale - typeInfo.defaultHeight / 2;
    const x = Math.max(0, Math.min(rawX, (scene?.width || 1920) - typeInfo.defaultWidth));
    const y = Math.max(0, Math.min(rawY, (scene?.height || 1080) - typeInfo.defaultHeight));
    addLayer(typeInfo, x, y);
  };

  // ─── Selected layer helpers ───────────────────────────────────────────────
  const selectedLayer = layers.find((l) => l.id === selectedLayerId) || null;

  const updateLayerField = (field, value) => {
    setLayers((prev) =>
      prev.map((l) => (l.id === selectedLayerId ? { ...l, [field]: value } : l))
    );
  };

  const saveSelectedLayer = () => {
    if (selectedLayer) saveLayer(selectedLayer);
  };

  // ─── Scene name edit ──────────────────────────────────────────────────────
  const commitNameEdit = () => {
    setEditingName(false);
    if (!nameDraft.trim()) return;
    const updated = { ...scene, name: nameDraft.trim() };
    setScene(updated);
    saveScene({ name: nameDraft.trim() });
  };

  // ─── Sound upload / remove ────────────────────────────────────────────────
  const handleSoundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSoundUploading(true);
    try {
      // Step 1: get presigned upload URL
      const urlRes = await fetch(
        `${config.API_URL}/overlay-scenes/${sceneId}/sound-upload-url?filename=${encodeURIComponent(file.name)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!urlRes.ok) throw new Error('Failed to get upload URL');
      const { upload_url, cdn_url, content_type } = await urlRes.json();

      // Step 2: upload directly to Spaces
      const uploadRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': content_type, 'x-amz-acl': 'public-read' },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Upload to storage failed');

      // Step 3: confirm with backend
      const confirmRes = await fetch(`${config.API_URL}/overlay-scenes/${sceneId}/sound-confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ cdn_url }),
      });
      if (confirmRes.ok) {
        const data = await confirmRes.json();
        setScene((s) => ({ ...s, sound_url: data.sound_url }));
        flashSaved();
      }
    } catch (err) {
      console.error('Error uploading sound:', err);
    } finally {
      setSoundUploading(false);
      if (soundInputRef.current) soundInputRef.current.value = '';
    }
  };

  const handleSoundRemove = async () => {
    try {
      await fetch(`${config.API_URL}/overlay-scenes/${sceneId}/sound`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setScene((s) => ({ ...s, sound_url: null }));
      flashSaved();
    } catch (err) {
      console.error('Error removing sound:', err);
    }
  };

  // ─── Tip Media layer upload / remove ─────────────────────────────────────
  const handleMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedLayer) return;
    setMediaUploading(true);
    try {
      const urlRes = await fetch(
        `${config.API_URL}/overlay-scenes/${sceneId}/sound-upload-url?filename=${encodeURIComponent(file.name)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!urlRes.ok) throw new Error('Failed to get upload URL');
      const { upload_url, cdn_url, content_type } = await urlRes.json();
      const uploadRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': content_type, 'x-amz-acl': 'public-read' },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');
      const updatedLayer = { ...selectedLayer, config: { ...selectedLayer.config, media_url: cdn_url } };
      setLayers((prev) => prev.map((l) => (l.id === selectedLayer.id ? updatedLayer : l)));
      await saveLayer(updatedLayer);
    } catch (err) {
      console.error('Error uploading tip media:', err);
    } finally {
      setMediaUploading(false);
      if (mediaInputRef.current) mediaInputRef.current.value = '';
    }
  };

  const handleMediaRemove = async () => {
    if (!selectedLayer) return;
    const updatedLayer = { ...selectedLayer, config: { ...selectedLayer.config, media_url: null } };
    setLayers((prev) => prev.map((l) => (l.id === selectedLayer.id ? updatedLayer : l)));
    await saveLayer(updatedLayer);
  };

  const obsUrl = scene?.creator_username
    ? `${window.location.origin}/overlay/scene/${scene.creator_username}/${toSlug(scene.name)}/${sceneId}`
    : `${window.location.origin}/overlay/scene/${sceneId}`;

  const copyObs = () => {
    navigator.clipboard.writeText(obsUrl);
    setCopiedObs(true);
    setTimeout(() => setCopiedObs(false), 2000);
  };

  // ─── Loading / null ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!scene) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] text-white flex items-center justify-center">
        <p className="text-gray-400">Scene not found.</p>
      </div>
    );
  }

  const canvasW = scene.width * canvasScale;
  const canvasH = scene.height * canvasScale;

  return (
    <div className="flex flex-col h-screen bg-[#0a0f1a] text-white overflow-hidden">
      {/* ── Top bar ── */}
      <header className="flex items-center gap-4 px-4 h-12 bg-[#111827] border-b border-gray-800/50 flex-shrink-0 z-20">
        <button
          onClick={() => navigate('/creator-dashboard')}
          className="text-gray-400 hover:text-white transition p-1"
          title="Back to dashboard"
        >
          <FaArrowLeft size={14} />
        </button>

        {editingName ? (
          <input
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={commitNameEdit}
            onKeyDown={(e) => { if (e.key === 'Enter') commitNameEdit(); if (e.key === 'Escape') { setEditingName(false); setNameDraft(scene.name); } }}
            className="bg-transparent border-b border-cyan-500 text-white text-sm focus:outline-none px-1 min-w-0 w-40"
          />
        ) : (
          <button
            onClick={() => { setEditingName(true); setNameDraft(scene.name); }}
            className="text-sm font-semibold hover:text-cyan-400 transition truncate max-w-[180px]"
            title="Click to rename"
          >
            {scene.name}
          </button>
        )}

        <span className="text-gray-600 text-xs flex-shrink-0">{scene.width} × {scene.height}</span>

        <div className="flex-1" />

        <div className="hidden sm:flex items-center gap-2 bg-[#0a0f1a] border border-gray-700 rounded-lg px-3 py-1.5 max-w-xs">
          <span className="text-xs text-gray-500 font-mono truncate">{obsUrl}</span>
          <button onClick={copyObs} className="text-gray-500 hover:text-cyan-400 transition flex-shrink-0">
            {copiedObs ? <FaCheck size={12} className="text-green-400" /> : <FaCopy size={12} />}
          </button>
        </div>

        {savedFlash && (
          <span className="text-xs text-green-400 flex-shrink-0">Saved</span>
        )}

        <button
          onClick={() => window.open(`${obsUrl}?test=1`, '_blank')}
          className="flex items-center gap-1.5 bg-yellow-600/20 hover:bg-yellow-600/30 border border-yellow-600/40 text-yellow-400 hover:text-yellow-300 text-xs font-semibold py-1.5 px-3 rounded-lg transition flex-shrink-0"
          title="Preview with mock data — no backend calls"
        >
          <FaFlask size={11} />
          <span>Test</span>
        </button>

        <button
          onClick={() => saveScene()}
          className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold py-1.5 px-4 rounded-lg transition flex-shrink-0"
        >
          Save
        </button>
      </header>

      {/* ── Body: left + canvas + right ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel */}
        <aside className="w-60 bg-[#111827] border-r border-gray-800/50 flex flex-col flex-shrink-0">
          {/* Widget palette */}
          <div className="px-3 pt-3 pb-2 border-b border-gray-800/50">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1.5">Widgets</p>
            <p className="text-[10px] text-gray-600 mb-2">Drag onto canvas to add</p>
            <div className="space-y-1.5">
              {LAYER_TYPES.map((t) => (
                <div
                  key={t.type}
                  draggable
                  onDragStart={(e) => handleWidgetDragStart(e, t)}
                  className="flex items-center gap-2 px-2.5 py-2 bg-[#0a0f1a] hover:bg-cyan-500/10 border border-gray-700/50 hover:border-cyan-700/50 rounded-lg cursor-grab active:cursor-grabbing transition select-none"
                >
                  <FaGripVertical size={10} className="text-gray-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-white truncate">{t.label}</p>
                    <p className="text-[10px] text-gray-500 truncate leading-tight mt-0.5">{t.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Layers list */}
          <div className="px-4 py-2 border-b border-gray-800/50">
            <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Layers</span>
          </div>
          <div className="flex-1 overflow-y-auto py-1">
            {layers.length === 0 ? (
              <p className="text-gray-600 text-xs text-center px-4 py-6 leading-relaxed">
                No layers yet.<br />Drag a widget onto the canvas.
              </p>
            ) : (
              [...layers].sort((a, b) => (b.z_index || 0) - (a.z_index || 0)).map((layer) => (
                <button
                  key={layer.id}
                  onClick={() => setSelectedLayerId(layer.id)}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition group ${
                    selectedLayerId === layer.id
                      ? 'bg-cyan-500/10 text-cyan-400'
                      : 'text-gray-300 hover:bg-white/5'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{layer.name}</p>
                    <p className="text-[10px] text-gray-600 mt-0.5">{layerTypeLabel(layer.type)}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteLayer(layer.id); }}
                    className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 transition ml-2 flex-shrink-0"
                    title="Delete layer"
                  >
                    <FaTrash size={10} />
                  </button>
                </button>
              ))
            )}
          </div>

        </aside>

        {/* Canvas area */}
        <div
          ref={canvasContainerRef}
          className="flex-1 flex items-center justify-center bg-[#0a0f1a] overflow-hidden select-none"
          onClick={() => setSelectedLayerId(null)}
        >
          <div
            ref={canvasRef}
            style={{
              width: canvasW,
              height: canvasH,
              position: 'relative',
              ...CHECKERBOARD_STYLE,
              flexShrink: 0,
              outline: isDragOver ? '2px dashed rgba(6,182,212,0.6)' : '2px solid transparent',
              transition: 'outline-color 0.15s',
            }}
            onClick={(e) => e.stopPropagation()}
            onDragOver={handleCanvasDragOver}
            onDragLeave={handleCanvasDragLeave}
            onDrop={handleCanvasDrop}
          >
            {isDragOver && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(6,182,212,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  zIndex: 100,
                }}
              >
                <span style={{ color: 'rgba(6,182,212,0.7)', fontSize: 13, fontWeight: 600 }}>
                  Drop to add widget
                </span>
              </div>
            )}

            {layers.map((layer) => {
              const isSelected = selectedLayerId === layer.id;
              return (
                <div
                  key={layer.id}
                  onMouseDown={(e) => handleLayerMouseDown(e, layer.id)}
                  style={{
                    position: 'absolute',
                    left: layer.x * canvasScale,
                    top: layer.y * canvasScale,
                    width: layer.width * canvasScale,
                    height: layer.height * canvasScale,
                    boxSizing: 'border-box',
                    border: isSelected ? '2px dashed #06b6d4' : '1px dashed rgba(6,182,212,0.25)',
                    borderRadius: 6,
                    cursor: 'grab',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 6,
                    background: isSelected ? 'rgba(6,182,212,0.12)' : 'rgba(6,182,212,0.05)',
                    userSelect: 'none',
                    transition: 'background 0.15s, border-color 0.15s',
                  }}
                >
                  <span style={{
                    fontSize: Math.max(7, 9 * canvasScale),
                    color: '#06b6d4',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    textAlign: 'center',
                    padding: '0 6px',
                  }}>
                    {layerTypeLabel(layer.type)}
                  </span>
                  <span style={{
                    fontSize: Math.max(6, 8 * canvasScale),
                    color: 'rgba(255,255,255,0.35)',
                    textAlign: 'center',
                    padding: '0 6px',
                  }}>
                    {LAYER_HINTS[layer.type] || layer.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right panel */}
        <aside className="w-70 bg-[#111827] border-l border-gray-800/50 flex flex-col flex-shrink-0" style={{ width: 280 }}>
          <div className="flex-1 overflow-y-auto">
            {!selectedLayer ? (
              /* Scene settings */
              <div className="p-4">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-4">Scene settings</p>
                <p className="text-xs text-gray-600 mb-5">Click a layer to edit it.</p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5">Name</label>
                    <input
                      type="text"
                      value={scene.name}
                      onChange={(e) => setScene((s) => ({ ...s, name: e.target.value }))}
                      onBlur={() => saveScene()}
                      className="w-full px-3 py-2 bg-[#0a0f1a] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none text-sm text-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">Width</label>
                      <input
                        type="number"
                        value={scene.width}
                        onChange={(e) => setScene((s) => ({ ...s, width: Number(e.target.value) }))}
                        onBlur={() => saveScene()}
                        min={100}
                        max={7680}
                        className="w-full px-3 py-2 bg-[#0a0f1a] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none text-sm text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">Height</label>
                      <input
                        type="number"
                        value={scene.height}
                        onChange={(e) => setScene((s) => ({ ...s, height: Number(e.target.value) }))}
                        onBlur={() => saveScene()}
                        min={100}
                        max={4320}
                        className="w-full px-3 py-2 bg-[#0a0f1a] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none text-sm text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5">Background</label>
                    <select
                      value={scene.background || 'transparent'}
                      onChange={(e) => {
                        const updated = { ...scene, background: e.target.value };
                        setScene(updated);
                        saveScene({ background: e.target.value });
                      }}
                      className="w-full px-3 py-2 bg-[#0a0f1a] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none text-sm text-white"
                    >
                      <option value="transparent">Transparent</option>
                      <option value="black">Black</option>
                      <option value="white">White</option>
                    </select>
                  </div>

                </div>
              </div>
            ) : (
              /* Layer settings */
              <div className="p-4">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-4">Layer settings</p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5">Name</label>
                    <input
                      type="text"
                      value={selectedLayer.name}
                      onChange={(e) => updateLayerField('name', e.target.value)}
                      onBlur={saveSelectedLayer}
                      className="w-full px-3 py-2 bg-[#0a0f1a] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none text-sm text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5">Type</label>
                    <div className="px-3 py-2 bg-[#0a0f1a] border border-gray-800 rounded-lg text-sm text-gray-500">
                      {layerTypeLabel(selectedLayer.type)}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">X</label>
                      <input
                        type="number"
                        value={selectedLayer.x}
                        onChange={(e) => updateLayerField('x', Number(e.target.value))}
                        onBlur={saveSelectedLayer}
                        className="w-full px-3 py-2 bg-[#0a0f1a] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none text-sm text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">Y</label>
                      <input
                        type="number"
                        value={selectedLayer.y}
                        onChange={(e) => updateLayerField('y', Number(e.target.value))}
                        onBlur={saveSelectedLayer}
                        className="w-full px-3 py-2 bg-[#0a0f1a] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none text-sm text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">Width</label>
                      <input
                        type="number"
                        value={selectedLayer.width}
                        onChange={(e) => updateLayerField('width', Number(e.target.value))}
                        onBlur={saveSelectedLayer}
                        className="w-full px-3 py-2 bg-[#0a0f1a] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none text-sm text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">Height</label>
                      <input
                        type="number"
                        value={selectedLayer.height}
                        onChange={(e) => updateLayerField('height', Number(e.target.value))}
                        onBlur={saveSelectedLayer}
                        className="w-full px-3 py-2 bg-[#0a0f1a] border border-gray-700 rounded-lg focus:border-cyan-500 focus:outline-none text-sm text-white"
                      />
                    </div>
                  </div>
                </div>

                {selectedLayer.type === 'tip_media' && (
                  <div className="mt-2">
                    <label className="block text-xs text-gray-400 mb-1.5">Media file</label>
                    {selectedLayer.config?.media_url ? (
                      <div className="flex items-center gap-2 px-2.5 py-2 bg-[#0a0f1a] border border-gray-700 rounded-lg">
                        <FaVolumeUp size={9} className="text-cyan-400 flex-shrink-0" />
                        <span className="text-[10px] text-gray-300 truncate flex-1">
                          {selectedLayer.config.media_url.split('/').pop()}
                        </span>
                        <button
                          onClick={handleMediaRemove}
                          className="text-gray-600 hover:text-red-400 transition flex-shrink-0"
                          title="Remove"
                        >
                          <FaTimes size={9} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => mediaInputRef.current?.click()}
                        disabled={mediaUploading}
                        className="w-full px-2.5 py-2 bg-[#0a0f1a] border border-dashed border-gray-700 hover:border-cyan-700 rounded-lg text-[10px] text-gray-500 hover:text-gray-300 transition disabled:opacity-50 text-left"
                      >
                        {mediaUploading ? 'Uploading…' : '+ Upload audio or video'}
                      </button>
                    )}
                    <p className="text-[10px] text-gray-600 mt-1.5 leading-tight">WebM = video · MP3/WAV/OGG = audio only</p>
                    <input
                      ref={mediaInputRef}
                      type="file"
                      accept=".webm,.mp3,.wav,.ogg,audio/*,video/webm"
                      className="hidden"
                      onChange={handleMediaUpload}
                    />
                  </div>
                )}

                {/* ── Live overlay test controls, per layer type ── */}
                {['tip_alert', 'tip_media'].includes(selectedLayer.type) && (
                  <div className="mt-4 border-t border-gray-800 pt-4">
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Test tips</p>
                    <div className="flex gap-1.5">
                      {[1, 5, 10, 20].map((amt) => (
                        <button
                          key={amt}
                          onClick={() => fireTipTest(amt)}
                          className="flex-1 bg-purple-600/20 border border-purple-600/40 text-purple-300 hover:bg-purple-600/30 text-[10px] font-bold py-1.5 rounded transition"
                        >
                          ${amt}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-600 mt-1">
                      {selectedLayer.type === 'tip_alert'
                        ? 'Fires a test tip alert on the live overlay'
                        : 'Plays this layer’s media on the live overlay'}
                    </p>
                  </div>
                )}

                {selectedLayer.type === 'recent_tips' && (
                  <div className="mt-4 border-t border-gray-800 pt-4">
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Test tips</p>
                    <div className="flex gap-1.5">
                      {[1, 5, 10, 20].map((amt) => (
                        <button
                          key={amt}
                          onClick={() => fireTestDonation(amt)}
                          className="flex-1 bg-purple-600/20 border border-purple-600/40 text-purple-300 hover:bg-purple-600/30 text-[10px] font-bold py-1.5 rounded transition"
                        >
                          ${amt}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-600 mt-1">Pushes a mock tip to the top of the live overlay's list</p>
                  </div>
                )}

                {selectedLayer.type === 'brick_counter' && (
                  <div className="mt-4 border-t border-gray-800 pt-4">
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Test bricks</p>
                    <p className="text-[11px] text-gray-400 mb-2">
                      Live test total: <span className="text-white font-bold">{testBricksTotal}</span>
                    </p>
                    <div className="flex gap-1.5 flex-wrap">
                      {[1, 5, 10, 50, 100].map((n) => (
                        <button
                          key={n}
                          onClick={() => fireTestBricks(n)}
                          className="bg-cyan-600/20 border border-cyan-600/40 text-cyan-300 hover:bg-cyan-600/30 text-[10px] font-bold py-1.5 px-2.5 rounded transition"
                        >
                          +{n}
                        </button>
                      ))}
                      <button
                        onClick={clearTestBricks}
                        className="bg-gray-600/20 border border-gray-600/40 text-gray-400 hover:bg-gray-600/30 text-[10px] font-bold py-1.5 px-2.5 rounded transition"
                      >
                        Reset
                      </button>
                    </div>
                    <p className="text-[10px] text-gray-600 mt-1">Overrides the count on the live overlay; Reset restores the real count</p>
                  </div>
                )}

                {/* ── Subathon Timer settings ── */}
                {selectedLayer.type === 'subathon_timer' && (
                  <div className="mt-4 space-y-4">
                    {/* Live Controls */}
                    <div className="border-t border-gray-800 pt-4">
                      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Live controls</p>
                      <div className="flex gap-1.5 mb-2">
                        <button onClick={() => subathonAction('start')} className="flex-1 bg-green-600/20 border border-green-600/40 text-green-400 hover:bg-green-600/30 text-[10px] font-bold py-1.5 rounded transition">START</button>
                        <button onClick={() => subathonAction('pause')} className="flex-1 bg-yellow-600/20 border border-yellow-600/40 text-yellow-400 hover:bg-yellow-600/30 text-[10px] font-bold py-1.5 rounded transition">PAUSE</button>
                        <button onClick={() => subathonAction('reset')} className="flex-1 bg-gray-600/20 border border-gray-600/40 text-gray-400 hover:bg-gray-600/30 text-[10px] font-bold py-1.5 rounded transition">RESET</button>
                      </div>
                      <div className="flex gap-1.5">
                        <input
                          type="number"
                          value={addMinutesInput}
                          onChange={(e) => setAddMinutesInput(Number(e.target.value))}
                          min={1}
                          className="flex-1 px-2 py-1.5 bg-[#0a0f1a] border border-gray-700 rounded text-xs text-white focus:border-cyan-500 focus:outline-none"
                        />
                        <button
                          onClick={() => subathonAction('add', addMinutesInput * 60)}
                          className="bg-cyan-600/20 border border-cyan-600/40 text-cyan-400 hover:bg-cyan-600/30 text-[10px] font-bold px-3 py-1.5 rounded transition whitespace-nowrap"
                        >
                          + ADD
                        </button>
                      </div>
                      <p className="text-[10px] text-gray-600 mt-1">Minutes to add</p>
                    </div>

                    {/* Test Tips */}
                    <div className="border-t border-gray-800 pt-4">
                      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Test tips</p>
                      <div className="flex gap-1.5">
                        {[1, 5, 10, 20].map((amt) => (
                          <button
                            key={amt}
                            onClick={() => subathonAction('add', (selectedLayer.config?.minutes_per_dollar ?? 1) * amt * 60)}
                            className="flex-1 bg-purple-600/20 border border-purple-600/40 text-purple-300 hover:bg-purple-600/30 text-[10px] font-bold py-1.5 rounded transition"
                          >
                            ${amt}
                          </button>
                        ))}
                      </div>
                      <p className="text-[10px] text-gray-600 mt-1">Simulates a live tip — adds time to the running timer</p>
                    </div>

                    {/* Timer settings */}
                    <div className="border-t border-gray-800 pt-4 space-y-3">
                      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Timer settings</p>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Start time (minutes)</label>
                        <input
                          type="number"
                          value={selectedLayer.config?.start_time_minutes ?? 60}
                          onChange={(e) => updateLayerConfig('start_time_minutes', Number(e.target.value))}
                          onBlur={() => { updateLayerConfig('seconds_remaining', (selectedLayer.config?.start_time_minutes ?? 60) * 60); saveSelectedLayer(); }}
                          min={1}
                          className="w-full px-2 py-1.5 bg-[#0a0f1a] border border-gray-700 rounded text-xs text-white focus:border-cyan-500 focus:outline-none"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-xs text-gray-400">Auto-start when source loads</label>
                        <button
                          onClick={() => { updateLayerConfig('auto_start', !(selectedLayer.config?.auto_start)); saveSelectedLayer(); }}
                          className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${selectedLayer.config?.auto_start ? 'bg-cyan-600' : 'bg-gray-700'}`}
                        >
                          <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${selectedLayer.config?.auto_start ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Minutes per $1 donated</label>
                        <input
                          type="number"
                          value={selectedLayer.config?.minutes_per_dollar ?? 1}
                          onChange={(e) => updateLayerConfig('minutes_per_dollar', Number(e.target.value))}
                          onBlur={saveSelectedLayer}
                          min={0}
                          step={0.5}
                          className="w-full px-2 py-1.5 bg-[#0a0f1a] border border-gray-700 rounded text-xs text-white focus:border-cyan-500 focus:outline-none"
                        />
                      </div>
                    </div>

                    {/* Appearance */}
                    <div className="border-t border-gray-800 pt-4 space-y-3">
                      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Appearance</p>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Timer size (px)</label>
                        <input
                          type="number"
                          value={selectedLayer.config?.timer_size ?? 40}
                          onChange={(e) => updateLayerConfig('timer_size', Number(e.target.value))}
                          onBlur={saveSelectedLayer}
                          min={12}
                          max={200}
                          className="w-full px-2 py-1.5 bg-[#0a0f1a] border border-gray-700 rounded text-xs text-white focus:border-cyan-500 focus:outline-none"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { key: 'timer_color', label: 'Timer color', def: '#ffffff' },
                          { key: 'chip_color', label: '+Min chip color', def: '#22d3ee' },
                          { key: 'accent_color', label: 'Accent / info', def: '#22d3ee' },
                        ].map(({ key, label, def }) => (
                          <div key={key}>
                            <label className="block text-[10px] text-gray-500 mb-1">{label}</label>
                            <div className="flex items-center gap-1.5">
                              <input
                                type="color"
                                value={selectedLayer.config?.[key] ?? def}
                                onChange={(e) => updateLayerConfig(key, e.target.value)}
                                onBlur={saveSelectedLayer}
                                className="w-7 h-7 rounded border border-gray-700 bg-transparent cursor-pointer p-0.5"
                              />
                              <span className="text-[10px] text-gray-500 truncate">{selectedLayer.config?.[key] ?? def}</span>
                            </div>
                          </div>
                        ))}
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-1">Corner radius</label>
                          <input
                            type="number"
                            value={selectedLayer.config?.corner_radius ?? 14}
                            onChange={(e) => updateLayerConfig('corner_radius', Number(e.target.value))}
                            onBlur={saveSelectedLayer}
                            min={0}
                            max={60}
                            className="w-full px-2 py-1.5 bg-[#0a0f1a] border border-gray-700 rounded text-[10px] text-white focus:border-cyan-500 focus:outline-none"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-1">Background</label>
                        <input
                          type="text"
                          value={selectedLayer.config?.bg_color ?? 'rgba(10,12,20,0.72)'}
                          onChange={(e) => updateLayerConfig('bg_color', e.target.value)}
                          onBlur={saveSelectedLayer}
                          placeholder="rgba(10,12,20,0.72)"
                          className="w-full px-2 py-1.5 bg-[#0a0f1a] border border-gray-700 rounded text-[10px] text-white focus:border-cyan-500 focus:outline-none font-mono"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="text-xs text-gray-400">Show event info on add</label>
                        <button
                          onClick={() => { updateLayerConfig('show_event_info', !(selectedLayer.config?.show_event_info ?? true)); saveSelectedLayer(); }}
                          className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${(selectedLayer.config?.show_event_info ?? true) ? 'bg-cyan-600' : 'bg-gray-700'}`}
                        >
                          <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${(selectedLayer.config?.show_event_info ?? true) ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => deleteLayer(selectedLayer.id)}
                  className="mt-6 w-full flex items-center justify-center gap-2 bg-red-900/30 hover:bg-red-900/50 border border-red-800/50 text-red-400 hover:text-red-300 font-medium py-2 px-3 rounded-lg transition text-sm"
                >
                  <FaTrash size={11} />
                  <span>Delete layer</span>
                </button>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
